import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, Redirect } from "react-router-dom";
import { FirebaseAuth, Storage } from "../../../../components/FirebaseAuth/firebase";
import { Elements, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Alert, Box, Paper, Container, Card, Typography, Stack, Slider, Autocomplete, Button, TextField, Divider, Grid, Chip, Stepper, Step, StepLabel } from "@mui/material";
import dayjs from 'dayjs';
import serviceJSON from "../../../../inc/services.json";
import Loader from '../../../../components/Loader';
import { getClientMetadata } from "../../../../libs/audit";

const OnboardingContractor = () => {
    const theme = useTheme();
    const maxDate = useMemo(() => dayjs().subtract(18, 'year'), []);
    const services = useMemo(() => serviceJSON.jobs, []);
    const [loading, setLoading] = useState(true);
    const [inSubmit, setInSubmit] = useState(false);
    const [accountExist, setAccountExist] = useState(false);
    const [canResubmitChanges, setCanResubmitChanges] = useState(false);
    const [reviewMessage, setReviewMessage] = useState("");
    const [stripe, setStripe] = useState(undefined);
    const [complete, setComplete] = useState(false);
    const [step, setStep] = useState(0);
    const [dob, setDob] = useState(maxDate);
    const [service, setService] = useState(services[0].code);
    const [serviceFamily, setServiceFamily] = useState(services[0].family || '');
    const [levels, setLevels] = useState(services[0].levels);
    const [level, setLevel] = useState(services[0].levels[0].code);
    const [price, setPrice] = useState(services[0].levels[0].minPrice);
    const [currency, setCurrency] = useState(services[0].levels[0].currency);
    const [min, setMin] = useState(services[0].levels[0].minPrice);
    const [max, setMax] = useState(services[0].levels[0].maxPrice);
    const [role, setRole] = useState('contractor');
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [address, setAddress] = useState({
        line1: '',
        line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US'
    });
    const [phone, setPhone] = useState('');
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeMeta, setResumeMeta] = useState(null);
    const [resumeUploading, setResumeUploading] = useState(false);
    const [resumeError, setResumeError] = useState("");
    const selectedService = services.find((item) => item.code === service) || null;
    const selectedLevel = levels.find((item) => item.code === level) || null;
    const setupSteps = ["Contact", "Identity", "Work", "Review"];

    const handleSliderChange = (event, newValue) => {
        setPrice(newValue);
    };

    const formatFileSize = (bytes = 0) => {
        const size = Number(bytes || 0);
        if (!size) return "";
        if (size < 1024 * 1024) return `${Math.ceil(size / 1024)} KB`;
        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleResumeUpload = async () => {
        const currentUser = FirebaseAuth.auth().currentUser;
        if (!currentUser || !resumeFile) return;

        const allowedTypes = [
            "application/pdf",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        if (!allowedTypes.includes(resumeFile.type)) {
            setResumeError("Upload a PDF or DOCX resume.");
            return;
        }
        if (resumeFile.size > 10 * 1024 * 1024) {
            setResumeError("Resume must be 10 MB or smaller.");
            return;
        }

        setResumeUploading(true);
        setResumeError("");
        try {
            const safeName = resumeFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
            const path = `resumes/${currentUser.uid}/current/${Date.now()}-${safeName}`;
            const uploadedAt = new Date();
            const metadata = {
                storage_path: path,
                file_name: resumeFile.name,
                content_type: resumeFile.type,
                size: resumeFile.size,
                uploaded_at: uploadedAt,
                status: "uploaded",
                updated_at: uploadedAt,
            };
            await Storage.ref(path).put(resumeFile, { contentType: resumeFile.type });
            await FirebaseAuth.firestore().collection("users").doc(currentUser.uid).set({ resume: metadata }, { merge: true });
            setResumeMeta(metadata);
            setResumeFile(null);
        } catch (error) {
            setResumeError(error?.message || "Resume upload failed.");
        } finally {
            setResumeUploading(false);
        }
    };

    const buildSubmissionPayload = (tosIp = "") => ({
        uid: FirebaseAuth.auth().currentUser.uid,
        email: FirebaseAuth.auth().currentUser.email,
        type: 'individual',
        fname,
        lname,
        dob: {
            dd: dob.format('DD'),
            mm: dob.format('MM'),
            yyyy: dob.format('YYYY')
        },
        address,
        phone,
        political_exposure: 'none',
        currency,
        service,
        service_family: serviceFamily,
        level,
        price,
        tos_ip: tosIp,
        resume: resumeMeta,
    });

    const handleSubmit = async () => {
        setInSubmit(true);
        setLoading(true);
        let tosIp = "";
        try {
            const metadata = await getClientMetadata();
            tosIp = String(metadata?.ip || "").trim();
        } catch (_error) {
            tosIp = "";
        }

        if (accountExist && canResubmitChanges) {
            try {
                const resubmit = FirebaseAuth.functions().httpsCallable("resubmitContractorOnboarding");
                await resubmit(buildSubmissionPayload(tosIp));
                setTimeout(function() {
                    window.location.replace("/");
                }, '1000');
            } catch (error) {
                setInSubmit(false);
                setLoading(false);
                setResumeError(error?.message || "Failed to resubmit profile changes.");
            }
            return;
        }

        try {
            const createContractorAccount = FirebaseAuth.functions().httpsCallable("createContractorAccount");
            await createContractorAccount(buildSubmissionPayload(tosIp));
            setTimeout(function() {
                window.location.reload();
            }, '1000');
            return true;
        } catch (error) {
            setInSubmit(false);
            setLoading(false);
            setResumeError(error?.message || "Failed to complete contractor onboarding.");
            return false;
        }
    }

    const getUserdata = useCallback(async () => {
        setLoading(true);
        const stripe = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        const userDoc = await FirebaseAuth.firestore().collection('users').doc(FirebaseAuth.auth().currentUser.uid).get();
        setStripe(stripe);
        if(userDoc.exists){
            const data = userDoc.data() || {};
            const hasAccount = !!data.account_created;
            const changesRequested = String(data.onboarding_status || data.review?.status || "").toLowerCase() === "changes_requested";
            const savedService = data.metadata?.service || services[0].code;
            const nextService = services.find((item) => item.code === savedService) || services[0];
            const savedLevel = data.metadata?.level || nextService.levels?.[0]?.code;
            const nextLevel = (nextService.levels || []).find((item) => item.code === savedLevel) || nextService.levels?.[0];
            const individual = data.individual || {};
            const individualAddress = data.address || individual.address || {};
            const dobData = individual.dob || {};
            const parsedDob = dobData.year && dobData.month && dobData.day
                ? dayjs(`${dobData.year}-${String(dobData.month).padStart(2, "0")}-${String(dobData.day).padStart(2, "0")}`)
                : maxDate;

            setRole(data.role || (hasAccount ? 'contractor' : null));
            setAccountExist(hasAccount);
            setCanResubmitChanges(hasAccount && changesRequested);
            setReviewMessage(String(data.review?.public_message || ""));
            setFname(data.fname || data.first_name || individual.first_name || "");
            setLname(data.lname || data.last_name || individual.last_name || "");
            setAddress({
                line1: individualAddress.line1 || "",
                line2: individualAddress.line2 || "",
                city: individualAddress.city || "",
                state: individualAddress.state || "",
                postal_code: individualAddress.postal_code || "",
                country: (individualAddress.country || data.country || "US").toUpperCase(),
            });
            setPhone(data.phone || individual.phone || "");
            setDob(parsedDob.isValid() ? parsedDob : maxDate);
            setService(nextService.code);
            setServiceFamily(nextService.family || data.metadata?.service_family || "");
            setLevels(nextService.levels || []);
            setLevel(nextLevel?.code || "");
            setCurrency(nextLevel?.currency || data.metadata?.currency || "usd");
            setPrice(data.metadata?.price ? Number(data.metadata.price) / 100 : nextLevel?.minPrice || 0);
            setMin(nextLevel?.minPrice || 0);
            setMax(nextLevel?.maxPrice || 0);
            setResumeMeta(data.resume || null);
            setComplete(hasAccount && changesRequested ? true : false);
        } else {
            setRole(null);
            setAccountExist(false);
            setCanResubmitChanges(false);
            setReviewMessage("");
            setResumeMeta(null);
        }
        setLoading(false);
    }, [maxDate, services]);

    useEffect(() => {
        getUserdata();
    }, [getUserdata]);

    return (
        <>
            {(loading) ? (
                <Loader variant="fullscreen" open={loading} label={inSubmit ? "Submitting contractor setup..." : "Preparing contractor setup..."} />
            ):( 
            <>
                {role !== 'contractor' && (
                    <Container style={{padding: '32px', maxWidth: '800px'}}>
                        <Paper variant="outlined" sx={{ px: 3, py: 3, borderWidth: 1, borderRadius: 4, backgroundColor: theme.palette.background.paper, boxShadow: "0 18px 60px rgba(60,64,67,0.08)"}}>
                            <Typography variant="h6" sx={{ mb: 1 }}>Contractor onboarding unavailable</Typography>
                            <Typography variant="body2" color="text.secondary">
                                This onboarding flow is for contractor accounts only.
                            </Typography>
                        </Paper>
                    </Container>
                )}
                {role === 'contractor' && accountExist && !canResubmitChanges && <Redirect to="/" />}
                {(!accountExist || canResubmitChanges) &&(
                    role === 'contractor' &&
                    <Container sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 2, sm: 3 }, maxWidth: "980px !important" }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 0,
                            py: 0,
                            width: '100%',
                            borderWidth: 1,
                            borderRadius: 4,
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: "0 18px 60px rgba(60,64,67,0.10)",
                            overflow: "hidden",
                            position: "relative",
                            "&:before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 5,
                              background: "linear-gradient(90deg, #05690D, #1976D2)",
                            },
                          }}
                        >
                            <Box sx={{width: '100%', textAlign: 'center'}}>
                                <Box sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3, md: 4 }, pb: 1, textAlign: "left" }}>
                                    <Chip label={canResubmitChanges ? "Changes requested" : "Contractor setup"} size="small" color={canResubmitChanges ? "info" : "default"} variant="outlined" sx={{ mb: 1.5, bgcolor: "#f8fafd", fontWeight: 700 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 850, color: "#30343b", letterSpacing: "-0.03em" }}>
                                        {canResubmitChanges ? "Update your contractor profile" : "Set up your contractor workspace"}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720, lineHeight: 1.6 }}>
                                        {canResubmitChanges
                                            ? "Review the admin request, update your details or resume, and resubmit your profile for approval."
                                            : "Add your contact details, choose your specialty, and set the hourly rate clients will see for matching."}
                                    </Typography>
                                    {canResubmitChanges && reviewMessage && (
                                        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                            {reviewMessage}
                                        </Alert>
                                    )}
                                    <Stepper activeStep={step} alternativeLabel sx={{ mt: 3, display: { xs: "none", sm: "flex" } }}>
                                        {setupSteps.map((label) => (
                                            <Step key={label}>
                                                <StepLabel>{label}</StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                                <Paper
                                role="dialog"
                                aria-modal="false"
                                aria-label="header"
                                square
                                variant="outlined"
                                tabIndex={-1}
                                sx={{
                                    position: 'relative',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    m: 0,
                                    p: { xs: 2.5, md: 4 },
                                    borderWidth: 0,
                                    backgroundColor: 'transparent'
                                }}
                                >
                                    {step===0&&(
                                    <>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            sx={{ justifyContent: 'space-between', gap: 2, pb: 3 }}
                                        >
                                            <Box
                                            sx={{ flexShrink: 1, textAlign: 'start' }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                                                    Contact information
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Confirm your legal name, address, and phone number for identity and payout setup. For details, review the <RouterLink to="/privacy"><u>Privacy Policy</u></RouterLink>.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <div style={{ marginBottom:'8px', textAlign: 'center', width: '100%'}}>
                                            <Elements stripe={stripe}>
                                                <AddressElement 
                                                    options={{
                                                        mode: 'shipping',
                                                        allowedCountries: ['US', 'CA', 'IN'],
                                                        fields: {
                                                            phone: 'always',
                                                        },
                                                        validation: {
                                                            phone: {
                                                                required: 'always',
                                                            },
                                                        },
                                                        display: {
                                                            name: 'split'
                                                        },
                                                        defaultValues: {
                                                            firstName: fname,
                                                            lastName: lname,
                                                            address: address,
                                                            phone: phone,
                                                        }
                                                    }} 
                                                    onChange={(event) => {
                                                        setComplete(event.complete);
                                                        if (event.value) {
                                                            setFname(event.value.firstName || "");
                                                            setLname(event.value.lastName || "");
                                                            setAddress(event.value.address || {});
                                                            setPhone(event.value.phone || "");
                                                        }
                                                    }}
                                                />
                                            </Elements>
                                        </div>
                                    </>
                                    )}
                                    {step===1&&(
                                    <>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            sx={{ justifyContent: 'space-between', gap: 2, pb: 3 }}
                                        >
                                            <Box
                                            sx={{ flexShrink: 1, textAlign: 'start' }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                                                    Identity check
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Add your date of birth so we can confirm eligibility and support compliance checks. For details, review the <RouterLink to="/privacy"><u>Privacy Policy</u></RouterLink>.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <div style={{ marginBottom:'8px', textAlign: 'center', width: '100%'}}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                {/* <p style={{ textAlign: 'left', marginTop: '8px', marginBottom: '8px', letterSpacing: 0.6, fontWeight: 400, fontSize: '14px' }}>Select your date of birth</p> */}
                                                <DatePicker
                                                    label="Date of Birth (MM/DD/YYYY)"
                                                    value={dob}
                                                    onChange={(newValue) => setDob(newValue)}
                                                    maxDate={maxDate}
                                                    disableFuture
                                                    sx={{ width: '100%', mb: 2 }}
                                                    slotProps={{
                                                        textField: { fullWidth: true, helperText: "You must be at least 18 years old" },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    </>
                                    )}
                                    {step===2&&(
                                    <>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            sx={{ justifyContent: 'space-between', gap: 2, pb: 3 }}
                                        >
                                            <Box
                                            sx={{ flexShrink: 1, textAlign: 'start' }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                                                    Work profile
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Choose your specialty, set your hourly rate, and upload your latest resume so we can generate your Work Profile.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Stack spacing={2} sx={{ textAlign: "left" }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                                                <Stack spacing={0.5} sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Role and rate</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        This helps us match you to relevant client requests.
                                                    </Typography>
                                                </Stack>
                                                <Grid container spacing={2} sx={{ mt: 0 }}>
                                                    <Grid item xs={12} md={8}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>Specialty role</Typography>
                                                    <Autocomplete fullWidth 
                                                        value={(service !== '')?(services.find(obj =>{
                                                            return obj.code === service
                                                        })):(null)}
                                                        size="small"
                                                        options={services}
                                                        disabled={false}
                                                        autoHighlight
                                                        getOptionLabel={(option) => option.label}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                {option.label}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password',
                                                                }}
                                                            />
                                                        )}
                                                        onChange={(event, newValue) => {
                                                            if(newValue && newValue.code){
                                                                setService(newValue.code);
                                                                setServiceFamily(newValue.family || '');
                                                                setLevels(newValue.levels);
                                                                setLevel(newValue.levels[0].code);
                                                                setCurrency(newValue.levels[0].currency);
                                                                setPrice(newValue.levels[0].minPrice);
                                                                setMin(newValue.levels[0].minPrice);
                                                                setMax(newValue.levels[0].maxPrice);
                                                            }
                                                        }}
                                                    />
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.75 }}>Seniority level</Typography>
                                                    <Autocomplete fullWidth 
                                                        value={(level !== '')?(levels.find(obj =>{
                                                            return obj.code === level
                                                        })):(null)}
                                                        size="small"
                                                        options={levels}
                                                        disabled={false}
                                                        autoHighlight
                                                        getOptionLabel={(option) => option.label}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                {option.label}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password',
                                                                }}
                                                            />
                                                        )}
                                                        onChange={(event, newValue) => {
                                                            if(newValue && newValue.code){
                                                                setLevel(newValue.code);
                                                                setCurrency(newValue.currency);
                                                                setPrice(newValue.minPrice);
                                                                setMin(newValue.minPrice);
                                                                setMax(newValue.maxPrice);
                                                            }
                                                        }}
                                                    />
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>Expected hourly rate</Typography>
                                                        <Card variant="outlined" sx={{ px: 0, py: 1.25, width: '100%', minHeight: 132, textAlign: 'center', display: 'block', borderRadius: 2 }}>
                                                        <Typography variant="h4" gutterBottom sx={{ mx: 2, my: 0, px: 2, py: 1, width: '-webkit-fill-available', fontWeight: 850 }}>
                                                            ${price}&nbsp;/hr
                                                        </Typography>
                                                        <Divider sx={{ m: 0, width: "100%" }}/>
                                                        <Slider
                                                        aria-label="Hourly-rate"
                                                        defaultValue={min}
                                                        valueLabelDisplay="auto"
                                                        step={0.5}
                                                        marks
                                                        min={min}
                                                        max={max}
                                                        sx={{ mx: 3, my: 1, width: '-webkit-fill-available' }}
                                                        onChange={handleSliderChange}
                                                        />
                                                    </Card>
                                                    </Grid>
                                                </Grid>
                                            </Paper>

                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50], textAlign: "left" }}>
                                            <Stack spacing={1.5}>
                                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5}>
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                            {resumeMeta?.file_name || resumeFile?.name || "No resume uploaded"}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {resumeMeta
                                                                ? `${String(resumeMeta.content_type || "").includes("pdf") ? "PDF" : "DOCX"} · ${formatFileSize(resumeMeta.size)} · uploaded`
                                                                : resumeFile
                                                                    ? `${resumeFile.type.includes("pdf") ? "PDF" : "DOCX"} · ${formatFileSize(resumeFile.size)} · ready`
                                                                    : "Accepted formats: PDF or DOCX, up to 10 MB."}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                        <Button variant="outlined" component="label" sx={{ textTransform: "none", borderRadius: 2 }}>
                                                            Choose file
                                                            <input
                                                                hidden
                                                                type="file"
                                                                accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                                                onChange={(event) => {
                                                                    setResumeError("");
                                                                    setResumeMeta(null);
                                                                    setResumeFile(event.target.files?.[0] || null);
                                                                }}
                                                            />
                                                        </Button>
                                                        {resumeFile && (
                                                            <Button
                                                                variant="contained"
                                                                onClick={handleResumeUpload}
                                                                disabled={resumeUploading}
                                                                sx={{ textTransform: "none", borderRadius: 2, fontWeight: 800 }}
                                                            >
                                                                {resumeUploading ? <Loader size={18} /> : "Upload"}
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                                {resumeError && <Alert severity="error">{resumeError}</Alert>}
                                                {resumeMeta && (
                                                    <Alert severity="success">
                                                        Resume uploaded. We will generate your Work Profile from this resume after account setup is submitted.
                                                    </Alert>
                                                )}
                                            </Stack>
                                        </Paper>
                                        </Stack>
                                    </>
                                    )}
                                    {step===3&&(
                                    <>
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            sx={{ justifyContent: 'space-between', gap: 2, pb: 3 }}
                                        >
                                            <Box
                                            sx={{ flexShrink: 1, textAlign: 'start' }}
                                            >
                                                <Typography variant="h6" sx={{ fontWeight: 850, color: "#30343b" }}>
                                                    Final review
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    Review your setup before submitting. You can go back and adjust anything that needs a second look.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Box sx={{ mb: 1, width: '100%' }}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
                                                <Stack spacing={1.25} sx={{ textAlign: 'left' }}>
                                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Contact Information</Typography>
                                                        <Stack spacing={0.4}>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{`${fname} ${lname}`.trim() || '-'}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{address.line1 || '-'}</Typography>
                                                            {address.line2 ? <Typography variant="body2" color="text.secondary">{address.line2}</Typography> : null}
                                                            <Typography variant="body2" color="text.secondary">
                                                                {[address.city, address.state, address.postal_code, address.country].filter(Boolean).join(', ') || '-'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">{phone || '-'}</Typography>
                                                        </Stack>
                                                    </Paper>

                                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Personal Information</Typography>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                            <Typography variant="body2" color="text.secondary">Date of birth</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{dob.format('MM-DD-YYYY')}</Typography>
                                                        </Stack>
                                                    </Paper>

                                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Work Information</Typography>
                                                        <Stack spacing={0.6}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="body2" color="text.secondary">Specialty</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedService?.label || service || '-'}</Typography>
                                                            </Stack>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="body2" color="text.secondary">Seniority</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedLevel?.label || level || '-'}</Typography>
                                                            </Stack>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Typography variant="body2" color="text.secondary">Expected hourly rate</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{`$${Number(price || 0).toFixed(2)} / hr`}</Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Paper>

                                                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>Resume</Typography>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                            <Typography variant="body2" color="text.secondary">Uploaded file</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{resumeMeta?.file_name || '-'}</Typography>
                                                        </Stack>
                                                    </Paper>
                                                </Stack>
                                            </Paper>
                                        </Box>
                                    </>
                                    )}
                                    <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    sx={{ justifyContent: 'space-between', gap: 2, direction: 'rtl', pt: 3 }}
                                    >
                                        <Stack
                                            direction={{ xs: 'row-reverse', sm: 'row',}}
                                            sx={{ gap: 2, flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' },}}
                                        >
                                            {step>=0&&step<3&&<Button size="medium" disabled={(step === 0 && !complete) || (step === 2 && !resumeMeta)} variant="contained" sx={{textTransform: 'none', fontWeight: 800, borderRadius: 2 }} onClick={() => setStep(step+1)}>Next</Button>}
                                            {step===3&&<Button size="medium" disabled={ inSubmit || !complete || !resumeMeta || service===undefined || level===undefined || price===0 } variant="contained" sx={{textTransform: 'none', fontWeight: 800, borderRadius: 2 }} onClick={handleSubmit}>{inSubmit?<Loader/>:(canResubmitChanges ? "Resubmit for review" : "Submit")}</Button>}
                                            {step>0&&step<=3&&<Button size="medium" variant="outlined" color="inherit" sx={{textTransform: 'none' , borderColor: theme.palette.grey[300], borderRadius: 2 }} onClick={() => setStep(step-1)}>Back</Button>}
                                        </Stack>
                                    </Stack>
                                </Paper>
                            </Box>
                        </Paper>
                    </Container>
                )}
            </>
        )}
        </>
    );
}

export default OnboardingContractor;
